/**
 * WhatsNewPage — public "Recent Updates" page (live API-driven).
 *
 * UX corrections shipped in this iteration (owner directive 2026-02-02):
 *   - Bilingual safe date formatting (no T00:00:00 ever)
 *   - Sorted newest → oldest
 *   - First 10 release cards expanded by default per page
 *   - Top + bottom Expand All / Collapse All controls
 *   - Pagination 15 per page (First / Prev / numbered / Next / Last)
 *   - Structured release detail rendered ONLY when upstream provides
 *     features / fixes / changes / improvements / security / notes
 *     (no fabrication)
 *   - ?release=vX.Y.Z deep-link still auto-expands + scrolls into view
 *     across pages — if the target release lives on a later page, the
 *     page is jumped to automatically
 *
 * Source of truth: live `/api/integrations/ecosystem/changelog`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Bug,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Info,
  Link2,
  Package,
  ShieldCheck,
  Sparkles,
  Tag,
  Zap,
} from "lucide-react";

import WhatsNewRoadmapTabs from "@/components/WhatsNewRoadmapTabs";
import SourceFreshnessBadge from "@/components/SourceFreshnessBadge";
import Tooltip from "@/components/Tooltip";
import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import PageSideAura from "@/components/motion/PageSideAura";
import FloatingPager from "@/components/FloatingPager";
import { useEcosystemDataset } from "@/lib/useEcosystemData";
import { formatPublicDate } from "@/lib/formatPublicDate";

const PAGE_SIZE = 15;
const DEFAULT_EXPANDED_COUNT = 10;

const SERVICE_LABELS = {
  japanetccard: "JapanETCcard.com",
  japantollreceipts: "JapanTollReceipts.jp",
  japanchangemoney: "JapanChangeMoney.com",
  "nojo-enterprise": "NoJo Enterprise",
};

const SERVICE_HEX = {
  japanetccard: "#9333EA",        // vibrant purple
  japantollreceipts: "#16A34A",   // vibrant green
  japanchangemoney: "#2563EB",    // vibrant blue
  "nojo-enterprise": "#4F46E5",   // indigo (bluish-purple) — parent
};

// Release-type pill colors — JTR Rendering Skeleton §5 contract.
// Spec hex values, applied via inline style so Tailwind JIT doesn't
// silently drop a class.
const RELEASE_TYPE_HEX = {
  "Feature Release": "#2563eb",   // blue-600
  "Operator Release": "#4f46e5",  // indigo-600
  "Security Release": "#9333ea",  // purple-600
  Patch: "#4b5563",               // gray-600
};
const releaseTypePillStyle = (type) => ({
  backgroundColor: RELEASE_TYPE_HEX[type] || RELEASE_TYPE_HEX["Feature Release"],
  color: "#ffffff",
});

function ServiceChip({ serviceId }) {
  if (!serviceId) return null;
  const hex = SERVICE_HEX[serviceId] || "#0F1D2D";
  const label = SERVICE_LABELS[serviceId] || serviceId;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full tracking-[0.16em] font-mono uppercase font-bold text-[10px] px-2 py-0.5"
      style={{ color: hex, border: `1px solid ${hex}44`, backgroundColor: `${hex}10` }}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: hex }} />
      {label}
    </span>
  );
}

function ExpandAllControls({ allExpanded, onExpandAll, onCollapseAll, isJa, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2" data-testid={`${testIdPrefix}-controls`}>
      <button
        type="button"
        onClick={onExpandAll}
        data-testid={`${testIdPrefix}-expand-all`}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
          allExpanded
            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
        }`}
        disabled={allExpanded}
      >
        <ChevronDown className="w-3.5 h-3.5" />
        {isJa ? "すべて展開" : "Expand All"}
      </button>
      <button
        type="button"
        onClick={onCollapseAll}
        data-testid={`${testIdPrefix}-collapse-all`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
      >
        <ChevronRight className="w-3.5 h-3.5" />
        {isJa ? "すべて閉じる" : "Collapse All"}
      </button>
    </div>
  );
}

function Pagination({ page, pageCount, onChange, isJa, total, shown }) {
  if (pageCount <= 1) return null;
  // Build numbered window: up to 5 numbered pages around current.
  const pages = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(pageCount, start + 4);
  for (let p = start; p <= end; p += 1) pages.push(p);

  const Btn = ({ to, label, ariaLabel, testId, disabled, active, children }) => (
    <button
      type="button"
      onClick={() => onChange(to)}
      disabled={disabled}
      aria-label={ariaLabel || label}
      data-testid={testId}
      className={[
        "inline-flex items-center justify-center min-w-[36px] h-9 px-2.5 rounded-full text-xs font-semibold transition-colors",
        active
          ? "bg-[#9333EA] text-white"
          : disabled
          ? "bg-white text-slate-300 cursor-not-allowed border border-slate-200"
          : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50",
      ].join(" ")}
    >
      {children || label}
    </button>
  );

  return (
    <nav
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-8"
      data-testid="whats-new-pagination"
      aria-label={isJa ? "ページ送り" : "Pagination"}
    >
      <p className="text-xs text-slate-500" data-testid="whats-new-pagination-summary">
        {isJa
          ? `${total}件中 ${(page - 1) * PAGE_SIZE + 1}〜${(page - 1) * PAGE_SIZE + shown}件を表示`
          : `Showing ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + shown} of ${total}`}
      </p>
      <div className="flex items-center gap-1.5">
        <Btn to={1} testId="page-first" disabled={page === 1} label={isJa ? "最初" : "First"}>
          <ChevronsLeft className="w-3.5 h-3.5" />
        </Btn>
        <Btn to={page - 1} testId="page-prev" disabled={page === 1} label={isJa ? "前へ" : "Previous"}>
          <ChevronLeft className="w-3.5 h-3.5" />
        </Btn>
        {pages.map((p) => (
          <Btn key={p} to={p} active={p === page} testId={`page-${p}`} label={String(p)} />
        ))}
        <Btn to={page + 1} testId="page-next" disabled={page === pageCount} label={isJa ? "次へ" : "Next"}>
          <ChevronRight className="w-3.5 h-3.5" />
        </Btn>
        <Btn to={pageCount} testId="page-last" disabled={page === pageCount} label={isJa ? "最後" : "Last"}>
          <ChevronsRight className="w-3.5 h-3.5" />
        </Btn>
      </div>
    </nav>
  );
}

// ────────────────────────────────────────────────────────────────────
// Per-type visual treatment for change blocks.
// Source: JTR Rendering Skeleton §7.1 — pixel-identical to JTR's own
// `/en/changelog` page. Every hex value is locked by the partner spec;
// changing these without the partner is a breach of the shared brand
// contract.
// ────────────────────────────────────────────────────────────────────
const CHANGE_TYPE_META = {
  features: {
    label:  { en: "New Feature",  ja: "新機能" },
    Icon:   Sparkles,
    border: "#bbf7d0",  // green-200
    bg:     "#dcfce7",  // green-100
    color:  "#16a34a",  // green-600
  },
  improvements: {
    label:  { en: "Improvement",  ja: "改善" },
    Icon:   Zap,
    border: "#bfdbfe",  // blue-200
    bg:     "#dbeafe",  // blue-100
    color:  "#2563eb",  // blue-600
  },
  fixes: {
    label:  { en: "Bug Fix",      ja: "バグ修正" },
    Icon:   Bug,
    border: "#fecaca",  // red-200
    bg:     "#fee2e2",  // red-100
    color:  "#dc2626",  // red-600
  },
  security: {
    label:  { en: "Security",     ja: "セキュリティ" },
    Icon:   ShieldCheck,
    border: "#e9d5ff",  // purple-200
    bg:     "#f3e8ff",  // purple-100
    color:  "#9333ea",  // purple-600
  },
  notes: {
    label:  { en: "Note",         ja: "ノート" },
    Icon:   Info,
    border: "#e2e8f0",  // slate-200
    bg:     "#f1f5f9",  // slate-100
    color:  "#475569",  // slate-600
  },
};

/**
 * ChangeBlockList — renders an array of {title, desc} items as
 * spec-compliant change blocks (icon + label + title + body).
 *
 * Accepts:
 *   - string item       → renders as title-only
 *   - { title, desc }   → renders with both
 *   - bilingual { en, ja } on either field
 *
 * Empty/missing arrays render nothing.
 */
function ChangeBlockList({ items, type, isJa, pickLang }) {
  if (!items || items.length === 0) return null;
  const meta = CHANGE_TYPE_META[type];
  if (!meta) return null;
  const { label, Icon, border, bg, color } = meta;
  const labelText = isJa ? label.ja : label.en;

  return (
    <ul className="space-y-2">
      {items.map((it, i) => {
        const isString = typeof it === "string";
        const rawTitle = isString ? it : (it.title || it.heading || "");
        const rawDesc  = isString ? "" : (it.desc || it.description || it.body || "");
        const title = typeof rawTitle === "object" ? pickLang(rawTitle) : rawTitle;
        const desc  = typeof rawDesc  === "object" ? pickLang(rawDesc)  : rawDesc;
        return (
          <li
            key={i}
            className="flex items-start gap-3 p-3 rounded-lg border"
            style={{ borderColor: border, backgroundColor: bg }}
          >
            <span
              aria-hidden
              className="p-1.5 rounded-lg bg-white inline-flex shrink-0"
              style={{ color }}
            >
              <Icon className="w-4 h-4" strokeWidth={2.2} />
            </span>
            <div className="flex-1 min-w-0">
              <div
                className="text-xs font-medium"
                style={{ color }}
              >
                {labelText}
              </div>
              {title && (
                <div className="font-semibold text-slate-900 text-sm leading-snug mt-0.5">
                  {title}
                </div>
              )}
              {desc && (
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  {desc}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function StructuredChangeList() { return null; }
// (Legacy stub — the new ChangeBlockList component above replaces this.
//  Kept as a no-op so any stale callers compile harmlessly.)


export default function WhatsNewPage({ lang }) {
  const isJa = lang === "ja";
  const dataset = useEcosystemDataset(lang);
  const location = useLocation();
  const navigate = useNavigate();

  const releaseParam = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("release") || "";
  }, [location.search]);

  const pageParam = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const p = parseInt(sp.get("page") || "1", 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [location.search]);

  const serviceParam = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const s = sp.get("service") || "all";
    return s;
  }, [location.search]);

  const allReleases = dataset.RELEASES;

  // Service filter chip set — only show services that have at least one
  // release in the current dataset. Canonical order owner-locked:
  // NoJo → JEC → JTR → JCM.
  const availableServices = useMemo(() => {
    const set = new Set();
    allReleases.forEach((r) => {
      if (r._serviceId) set.add(r._serviceId);
    });
    const CANONICAL = ["nojo-enterprise", "japanetccard", "japantollreceipts", "japanchangemoney"];
    const ordered = CANONICAL.filter((s) => set.has(s));
    // Append any unknown service ids at the end (defensive)
    Array.from(set).forEach((s) => {
      if (!ordered.includes(s)) ordered.push(s);
    });
    return ["all", ...ordered];
  }, [allReleases]);

  const RELEASES = useMemo(() => {
    if (serviceParam === "all") return allReleases;
    return allReleases.filter((r) => r._serviceId === serviceParam);
  }, [allReleases, serviceParam]);

  const setService = (svc) => {
    const sp = new URLSearchParams(location.search);
    if (svc === "all") sp.delete("service");
    else sp.set("service", svc);
    // Reset to page 1 whenever the service filter changes.
    sp.delete("page");
    sp.delete("release");
    navigate({ search: sp.toString() }, { replace: false });
  };

  const total = RELEASES.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Auto-jump to the page that contains the deep-linked release.
  const computedPage = useMemo(() => {
    if (!releaseParam) return Math.min(pageParam, pageCount);
    const idx = RELEASES.findIndex((r) => r.version === releaseParam);
    if (idx < 0) return Math.min(pageParam, pageCount);
    return Math.floor(idx / PAGE_SIZE) + 1;
  }, [releaseParam, pageParam, pageCount, RELEASES]);

  const [page, setPage] = useState(computedPage);
  useEffect(() => {
    setPage(computedPage);
  }, [computedPage]);

  const visible = useMemo(() => {
    const startIdx = (page - 1) * PAGE_SIZE;
    return RELEASES.slice(startIdx, startIdx + PAGE_SIZE);
  }, [RELEASES, page]);

  // Track per-card expansion. Reset on page change so the new page's
  // first 10 are expanded by default. The deep-linked release is always
  // expanded if it's visible on this page.
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    const initial = {};
    visible.forEach((r, i) => {
      if (i < DEFAULT_EXPANDED_COUNT) initial[r.version] = true;
      if (releaseParam && r.version === releaseParam) initial[r.version] = true;
    });
    setExpanded(initial);
  }, [visible, releaseParam]);

  const allVisibleExpanded = visible.length > 0 && visible.every((r) => expanded[r.version]);

  const expandAll = () => {
    const next = {};
    visible.forEach((r) => { next[r.version] = true; });
    setExpanded(next);
  };
  const collapseAll = () => setExpanded({});

  const [copied, setCopied] = useState(false);
  const [copiedRelease, setCopiedRelease] = useState("");
  const releaseRefs = useRef({});

  // Title + meta are owned by the <SEO> component — Helmet handles updates.

  // Scroll-into-view + highlight for ?release=
  useEffect(() => {
    if (!releaseParam || !dataset.loaded) return;
    const timer = setTimeout(() => {
      const el = releaseRefs.current[releaseParam];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#9333EA]", "ring-offset-2");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-[#9333EA]", "ring-offset-2");
        }, 2400);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [releaseParam, dataset.loaded, page]);

  const setPageAndUrl = (next) => {
    const safe = Math.max(1, Math.min(pageCount, next));
    setPage(safe);
    const sp = new URLSearchParams(location.search);
    if (safe === 1) sp.delete("page");
    else sp.set("page", String(safe));
    sp.delete("release");
    navigate({ pathname: location.pathname, search: sp.toString() }, { replace: false });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleCopyLink = async () => {
    if (typeof window === "undefined") return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // silent
    }
  };

  const copyReleaseLink = (version) => {
    if (!version) return;
    const url = `${window.location.origin}/${lang}/whats-new?release=${encodeURIComponent(version)}`;
    navigate(`/${lang}/whats-new?release=${encodeURIComponent(version)}`, { replace: false });
    try {
      navigator.clipboard.writeText(url);
      setCopiedRelease(version);
      setTimeout(() => setCopiedRelease(""), 1800);
    } catch {
      // silent
    }
  };

  const pickLang = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    return field[lang] || field.en || field.ja || "";
  };

  // Stats across the FULL dataset (not just the current page).
  // JTR-aligned tiles: Releases · Changes · Features · Fixes
  const totalFeatures = RELEASES.reduce((s, r) => s + (r.features?.length || 0), 0);
  const totalFixes = RELEASES.reduce((s, r) => s + (r.fixes?.length || 0), 0);
  const totalImprovements = RELEASES.reduce((s, r) => s + (r.improvements?.length || 0), 0);
  const totalSecurity = RELEASES.reduce((s, r) => s + (r.security?.length || 0), 0);
  const totalChanges =
    totalFeatures + totalFixes + totalImprovements + totalSecurity +
    RELEASES.reduce((s, r) => s + (r.changes?.length || 0) + (r.notes?.length || 0), 0);

  // Count of unique services contributing to the current view.
  const servicesContributing = new Set(
    RELEASES.map((r) => r._serviceId).filter(Boolean),
  ).size;

  // Releases shipped in the last 30 days (relative to today). Uses the
  // canonical ISO `sortDate` produced by the backend normalizer.
  const releasesThisMonth = (() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return RELEASES.filter((r) => {
      const ts = r.sortDate ? new Date(r.sortDate).getTime() : NaN;
      return !Number.isNaN(ts) && ts >= cutoff;
    }).length;
  })();

  // Latest release date formatted in the page's language.
  const latestDateDisplay = RELEASES[0]
    ? formatPublicDate(RELEASES[0].sortDate || RELEASES[0].date, lang)
    : "";

  // Per-service latest version chips for the hero — owner directive:
  // surface every operated service's most recent shipped version side
  // by side so visitors can spot any change at a glance.
  const SERVICE_CHIP_ORDER = [
    "nojo-enterprise",
    "japanetccard",
    "japantollreceipts",
    "japanchangemoney",
  ];
  const SERVICE_SHORT_LABEL = {
    "nojo-enterprise": "NoJo",
    japanetccard: "JEC",
    japantollreceipts: "JTR",
    japanchangemoney: "JCM",
  };
  const latestVersionByService = SERVICE_CHIP_ORDER.map((sid) => {
    const r = RELEASES.find((x) => x._serviceId === sid);
    return r ? { sid, version: r.version, latest: r === RELEASES[0] } : null;
  }).filter(Boolean);

  // Adaptive stat tiles — JTR-aligned set: Releases · Changes · Features · Fixes
  const statTiles = [
    {
      n: RELEASES.length,
      l: isJa ? "リリース" : "Releases",
      color: "text-[#7C3AED]",
      tid: "stat-releases",
      show: true,
    },
    {
      n: totalChanges,
      l: isJa ? "変更内容" : "Changes",
      color: "text-[#2563EB]",
      tid: "stat-changes",
      show: totalChanges > 0,
    },
    {
      n: totalFeatures,
      l: isJa ? "新機能" : "Features",
      color: "text-[#16A34A]",
      tid: "stat-features",
      show: totalFeatures > 0,
    },
    {
      n: totalFixes,
      l: isJa ? "バグ修正" : "Fixes",
      color: "text-[#DC2626]",
      tid: "stat-fixes",
      show: true,
    },
  ].filter((s) => s.show);
  const gridCols =
    statTiles.length >= 4
      ? "grid-cols-4"
      : statTiles.length === 3
      ? "grid-cols-3"
      : statTiles.length === 2
      ? "grid-cols-2"
      : "grid-cols-1";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="whats-new-page">
      <SEO {...PAGE_SEO.whatsNew[lang]} path={`/${lang}/whats-new`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "最新情報" : "What's New", url: `/${lang}/whats-new` },
        ])}
      />
      <WhatsNewRoadmapTabs />
      <div>
        {/* HERO — JTR-aligned purple gradient */}
        <div
          className="relative text-white overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #6D28D9 0%, #9333EA 35%, #C026D3 65%, #9333EA 100%)",
          }}
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              background:
                "radial-gradient(900px 320px at 20% 0%, rgba(255,255,255,0.18), transparent 60%), radial-gradient(700px 280px at 80% 100%, rgba(255,255,255,0.12), transparent 60%)",
            }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg flex-shrink-0 ring-1 ring-white/30" aria-hidden>
                <Package className="w-5 h-5 text-white" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight text-white">
                  {isJa ? "最新情報" : "What's New"}
                </h1>
                <p className="text-white/90 text-xs sm:text-sm mt-1 leading-relaxed">
                  <span>
                    {isJa
                      ? "公開が承認された更新情報を追跡"
                      : "Track approved public updates across the operated services"}
                  </span>
                  {latestDateDisplay && (
                    <>
                      <span className="text-white/60 mx-1.5">·</span>
                      <span className="text-white/90">
                        {isJa ? `最終更新: ${latestDateDisplay}` : `Last Updated: ${latestDateDisplay}`}
                      </span>
                    </>
                  )}
                  {dataset.stale && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] font-mono uppercase bg-white/20 text-white/90 ring-1 ring-white/30">
                      {isJa ? "キャッシュ" : "cached"}
                    </span>
                  )}
                </p>
                {/* Per-service latest-version chips — single line on every
                    viewport. Pills are aggressively compacted on mobile
                    (smaller text, tighter padding, slimmer "New" badge)
                    so all three fit even on a 320px phone. */}
                {latestVersionByService.length > 0 && (
                  <div
                    className="mt-2 flex flex-nowrap items-center gap-1 sm:gap-1.5"
                    data-testid="whats-new-version-chips"
                  >
                    {latestVersionByService.map((entry) => (
                      <Link
                        key={entry.sid}
                        to={`/${lang}/whats-new`}
                        data-testid={`whats-new-version-chip-${entry.sid}`}
                        className="inline-flex items-center gap-0.5 sm:gap-1.5 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-white/20 hover:bg-white/30 ring-1 ring-white/30 text-white text-[9.5px] sm:text-[11px] font-semibold tracking-tight backdrop-blur-sm transition-colors whitespace-nowrap shrink-0"
                        title={`${SERVICE_LABELS[entry.sid] || entry.sid} · ${entry.version}`}
                      >
                        <span className="opacity-90">
                          {SERVICE_SHORT_LABEL[entry.sid] || entry.sid}
                        </span>
                        <span className="font-mono font-bold">{entry.version}</span>
                        {entry.latest && (
                          <span className="text-[7.5px] sm:text-[9px] tracking-[0.12em] sm:tracking-[0.14em] font-bold uppercase bg-white/30 px-1 rounded-full leading-none py-0.5">
                            {isJa ? "最新" : "New"}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
                <SourceFreshnessBadge
                  sourceFreshness={dataset.sourceFreshness}
                  lang={lang}
                  className="mt-1.5 text-white/80"
                />
              </div>
              <button
                type="button"
                onClick={handleCopyLink}
                className="inline-flex flex-shrink-0 items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-xs font-semibold rounded-full transition-colors ring-1 ring-white/30 self-start"
                data-testid="whats-new-copy-link"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                    <span className="hidden sm:inline">
                      {isJa ? "コピーしました" : "Copied!"}
                    </span>
                  </>
                ) : (
                  <>
                    <Link2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                    <span className="hidden sm:inline">
                      {isJa ? "リンクをコピー" : "Copy Link"}
                    </span>
                    <span className="sm:hidden">
                      {isJa ? "コピー" : "Copy"}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            MIDDLE CONTENT — wrapped with a subtle side aura sitting in
            the page gutters (full viewport width container) so the
            purple/blue glow reads on the right and left of the central
            reading column, NOT over the hero above or the safety
            footer below.
            ═══════════════════════════════════════════════════════════ */}
        <div className="relative isolate" data-testid="whats-new-middle">
          <PageSideAura testId="whats-new-side-aura" />

        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Service filter chips — owner directive: parity with Roadmap.
              Mobile (default): chips wrap onto multiple rows so all four
              services stay visible without horizontal scrolling — owner
              fix 2026-02-24 (previously `overflow-x-auto` hid JEC + JTR
              chips behind the right edge on 390px viewports, making the
              page look like it was filtered to NoJo only). At ≥sm we
              keep a single overflow-x-auto row to preserve the original
              roomy desktop layout. */}
          {availableServices.length > 2 && (
            <div
              role="group"
              aria-labelledby="whats-new-service-filter-label"
              className="flex flex-wrap items-center gap-2 sm:gap-3 sm:flex-nowrap sm:overflow-x-auto overflow-y-visible py-2 mb-3 border-b border-slate-200 pb-4"
              data-testid="whats-new-service-filter"
            >
              <span
                id="whats-new-service-filter-label"
                className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap w-full sm:w-auto sm:min-w-[120px]"
              >
                {isJa ? "サイトで絞り込み" : "Filter by Site"}
              </span>
              {availableServices.map((svc) => {
                const isActive = serviceParam === svc;
                const label =
                  svc === "all"
                    ? (isJa ? "すべて" : "All")
                    : SERVICE_LABELS[svc] || svc;
                const hex = svc === "all" ? "#663399" : (SERVICE_HEX[svc] || "#663399");
                const softMap = {
                  "#663399": { soft: "#F3E8FF", ring: "#E9D5FF" }, // logo purple
                  "#9333EA": { soft: "#F3E8FF", ring: "#E9D5FF" }, // purple
                  "#16A34A": { soft: "#DCFCE7", ring: "#BBF7D0" }, // green
                  "#2563EB": { soft: "#DBEAFE", ring: "#BFDBFE" }, // blue
                  "#4F46E5": { soft: "#E0E7FF", ring: "#C7D2FE" }, // indigo
                };
                const tint = softMap[hex] || softMap["#663399"];
                const tipAccent = svc === "all"
                  ? "purple"
                  : svc === "japanetccard" ? "purple"
                  : svc === "japantollreceipts" ? "green"
                  : svc === "japanchangemoney" ? "blue"
                  : "indigo";
                const tipText = svc === "all"
                  ? (isJa ? "全ての更新を表示" : "Show all updates")
                  : (isJa ? `${label}の更新のみ表示` : `Show ${label} updates only`);
                return (
                  <Tooltip
                    key={svc}
                    label={tipText}
                    accent={tipAccent}
                    placement="top"
                    testId={`whats-new-service-filter-${svc}-tip`}
                  >
                  <button
                    type="button"
                    onClick={() => setService(svc)}
                    data-testid={`whats-new-service-filter-${svc}`}
                    aria-pressed={isActive}
                    className={`shrink-0 px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition-all duration-200 inline-flex items-center gap-1.5 ${
                      isActive
                        ? "hover:-translate-y-0.5"
                        : "hover:-translate-y-0.5 hover:shadow-md hover:scale-[1.03]"
                    } active:scale-95`}
                    style={
                      isActive
                        ? {
                            backgroundColor: hex,
                            color: "#ffffff",
                            border: `1px solid ${hex}`,
                            boxShadow: `0 6px 18px -8px ${hex}99`,
                          }
                        : {
                            backgroundColor: tint.soft,
                            color: hex,
                            border: `1px solid ${tint.ring}`,
                          }
                    }
                  >
                    {svc !== "all" && (
                      <span
                        aria-hidden
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: isActive ? "white" : hex }}
                      />
                    )}
                    {label}
                  </button>
                  </Tooltip>
                );
              })}
            </div>
          )}

          {/* Stats row */}
          {/* Stats row — JTR-aligned: Releases · Changes · Features · Fixes */}
          <div
            className={`grid ${gridCols} gap-2 sm:gap-3 mb-6`}
            data-testid="whats-new-stats"
          >
            {statTiles.map((s) => {
              const numStr = String(s.n);
              // Scale the displayed number size based on digit count so
              // 4-digit values (e.g. 1146) don't bleed into the right edge
              // of the tile on narrow viewports.
              const numClass =
                numStr.length >= 4
                  ? "text-xl sm:text-2xl"
                  : numStr.length === 3
                  ? "text-2xl sm:text-3xl"
                  : "text-2xl sm:text-3xl";
              return (
                <div
                  key={s.tid}
                  data-testid={s.tid}
                  className="group bg-white rounded-xl px-2 sm:px-3 py-3 sm:py-4 border border-slate-200 text-center shadow-[0_1px_2px_rgba(15,29,45,0.04)] hover:-translate-y-0.5 hover:shadow-[0_10px_24px_-12px_rgba(15,29,45,0.18)] transition-all duration-200"
                >
                  <div className={`${numClass} font-bold ${s.color} tabular-nums tracking-tight leading-none transition-transform duration-200 group-hover:scale-105`}>
                    {s.n}
                  </div>
                  <div className="text-[10px] sm:text-[11px] text-slate-500 mt-1">{s.l}</div>
                </div>
              );
            })}
          </div>

          {/* VERSION NUMBERING explainer — JTR parity */}
          <div
            data-testid="whats-new-version-legend"
            className="rounded-xl bg-[#F5F3FF] ring-1 ring-[#DDD6FE] px-4 sm:px-5 py-4 mb-6"
          >
            <div className="text-[11px] tracking-[0.22em] font-mono uppercase font-bold text-[#6D28D9] mb-3">
              {isJa ? "バージョン番号の見方" : "Version Numbering"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {[
                { label: isJa ? "メジャー" : "Major", code: "X.0.0", desc: isJa ? "大規模リリース・互換性の変更" : "Big releases, breaking changes", color: "#7C3AED", bg: "#EDE9FE" },
                { label: isJa ? "マイナー" : "Minor", code: "1.X.0", desc: isJa ? "新機能・新規実装" : "New features, deployments", color: "#2563EB", bg: "#DBEAFE" },
                { label: "Patch", code: "1.0.X", desc: isJa ? "バグ修正・軽微な調整" : "Bug fixes, small tweaks", color: "#475569", bg: "#E2E8F0" },
              ].map((v) => (
                <div key={v.label} className="flex items-start gap-2.5">
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide flex-shrink-0 mt-0.5"
                    style={{ color: v.color, backgroundColor: v.bg }}
                  >
                    {v.label}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[12.5px] text-slate-700 font-semibold leading-tight">
                      <span className="font-mono">{v.code}</span>
                      <span className="text-slate-400 mx-1">·</span>
                      <span className="text-slate-600 font-medium">{v.desc}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top expand-all controls */}
          {visible.length > 0 && (
            <div className="flex items-center justify-between mb-4">
              <ExpandAllControls
                allExpanded={allVisibleExpanded}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
                isJa={isJa}
                testIdPrefix="whats-new-top"
              />
              <span className="text-[11px] text-slate-500 hidden sm:block">
                {isJa
                  ? `ページ ${page} / ${pageCount}`
                  : `Page ${page} of ${pageCount}`}
              </span>
            </div>
          )}

          {/* Empty state */}
          {dataset.loaded && total === 0 && (
            <p className="text-[13px] text-slate-500 py-12 text-center" data-testid="whats-new-empty">
              {isJa ? "表示できる更新はありません。" : "No updates to show right now."}
            </p>
          )}

          {/* Release cards (15 per page) */}
          <div className="space-y-4" data-testid="whats-new-list">
            {visible.map((release) => {
              const isOpen = !!expanded[release.version];
              const serviceId = release._serviceId;
              const displayDate = formatPublicDate(release.sortDate || release.date, lang);
              const hasStructuredDetail =
                (release.features && release.features.length > 0) ||
                (release.fixes && release.fixes.length > 0) ||
                (release.improvements && release.improvements.length > 0) ||
                (release.security && release.security.length > 0) ||
                (release.notes && release.notes.length > 0) ||
                (release.changes && release.changes.length > 0);

              return (
                <div
                  key={release.version}
                  ref={(el) => {
                    if (release.version) releaseRefs.current[release.version] = el;
                  }}
                  id={release.version ? `release-${release.version}` : undefined}
                  data-testid={`release-${release.version}`}
                  className={`border rounded-xl overflow-hidden scroll-mt-32 transition-shadow ${
                    release.latest ? "border-[#9333EA]/40 shadow-md" : "border-slate-200"
                  }`}
                >
                  {serviceId && (
                    <span
                      aria-hidden
                      className="block h-1 w-full"
                      style={{ backgroundColor: SERVICE_HEX[serviceId] || "#0F1D2D" }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setExpanded((p) => ({ ...p, [release.version]: !p[release.version] }))}
                    data-testid={`release-toggle-${release.version}`}
                    aria-expanded={isOpen}
                    aria-controls={`release-body-${release.version}`}
                    className={`w-full p-4 flex items-start justify-between gap-3 sm:gap-4 text-left transition-colors ${
                      release.latest
                        ? "bg-gradient-to-r from-[#9333EA]/5 to-[#C026D3]/5"
                        : "bg-white hover:bg-slate-50"
                    }`}
                  >
                    {/* LEFT COLUMN — version (line 1) + service URL chip (line 2) */}
                    <div className="flex flex-col gap-1.5 min-w-0 shrink-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Tag className={`w-5 h-5 ${release.latest ? "text-[#9333EA]" : "text-slate-400"}`} />
                        <span
                          className={`text-base sm:text-xl font-bold ${
                            release.latest ? "text-[#9333EA]" : "text-slate-700"
                          }`}
                        >
                          {release.version}
                        </span>
                        {release.latest && (
                          <span className="px-2 py-0.5 bg-[#9333EA] text-white text-[10px] font-semibold uppercase tracking-wider rounded-full">
                            {isJa ? "最新" : "Latest"}
                          </span>
                        )}
                        <span
                          className="hidden sm:inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                          style={releaseTypePillStyle(release.type)}
                        >
                          {release.type}
                        </span>
                      </div>
                      {serviceId && (
                        <div className="flex">
                          <ServiceChip serviceId={serviceId} />
                        </div>
                      )}
                    </div>

                    {/* RIGHT COLUMN — title + date (now has full breathing room) */}
                    <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0 ml-auto">
                      <div className="text-right hidden sm:block min-w-0 max-w-[520px] lg:max-w-[640px]">
                        <div className="text-sm font-medium text-slate-900 line-clamp-2 leading-snug">
                          {pickLang(release.title)}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1 justify-end mt-0.5">
                          <Calendar className="w-3 h-3" />
                          {displayDate}
                        </div>
                      </div>
                      {isOpen ? (
                        <ChevronDown className="w-5 h-5 text-slate-400 mt-0.5" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400 mt-0.5" />
                      )}
                    </div>
                  </button>
                  <div className="sm:hidden px-4 pb-3 -mt-1 text-[12.5px] font-medium text-slate-700 leading-snug">
                    {pickLang(release.title)}
                  </div>
                  <div className="sm:hidden px-4 pb-3 text-[10.5px] text-slate-500 flex items-center gap-1">
                    <Calendar className="w-2.5 h-2.5" strokeWidth={2.4} />
                    {displayDate}
                  </div>

                  {isOpen && (
                    <div
                      id={`release-body-${release.version}`}
                      className="p-4 bg-white border-t border-slate-100"
                    >
                      <p className="text-slate-600 mb-2 leading-relaxed text-sm">
                        <span className="text-[10px] tracking-[0.18em] font-mono uppercase font-bold text-slate-400 block mb-1">
                          {isJa ? "概要" : "Summary"}
                        </span>
                        {pickLang(release.desc)}
                      </p>

                      {/* Per-type change blocks — stable render order
                          per JTR Rendering Skeleton §7.2:
                          features → improvements → fixes → security → notes */}
                      <div className="mt-2 space-y-2">
                        <ChangeBlockList
                          items={release.features}
                          type="features"
                          isJa={isJa}
                          pickLang={pickLang}
                        />
                        <ChangeBlockList
                          items={release.improvements}
                          type="improvements"
                          isJa={isJa}
                          pickLang={pickLang}
                        />
                        <ChangeBlockList
                          items={release.fixes}
                          type="fixes"
                          isJa={isJa}
                          pickLang={pickLang}
                        />
                        <ChangeBlockList
                          items={release.security}
                          type="security"
                          isJa={isJa}
                          pickLang={pickLang}
                        />
                        <ChangeBlockList
                          items={release.notes}
                          type="notes"
                          isJa={isJa}
                          pickLang={pickLang}
                        />
                      </div>

                      <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                        {release.version && (
                          <button
                            type="button"
                            onClick={() => copyReleaseLink(release.version)}
                            data-testid={`release-deep-link-${release.version}`}
                            className="inline-flex items-center gap-1 hover:text-[#0E5FB0] transition-colors"
                          >
                            <Link2 className="w-3 h-3" />
                            {copiedRelease === release.version
                              ? isJa
                                ? "リンクをコピーしました"
                                : "Link copied"
                              : isJa
                              ? "このリリースへのリンク"
                              : "Link to this release"}
                          </button>
                        )}
                        {serviceId && (
                          <span className="ml-auto text-slate-400">
                            {isJa ? "関連サービス" : "Related service"}: {SERVICE_LABELS[serviceId] || serviceId}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom expand-all controls + pagination */}
          {visible.length > 0 && (
            <div className="flex items-center justify-between mt-6">
              <ExpandAllControls
                allExpanded={allVisibleExpanded}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
                isJa={isJa}
                testIdPrefix="whats-new-bottom"
              />
            </div>
          )}

          <Pagination
            page={page}
            pageCount={pageCount}
            onChange={setPageAndUrl}
            isJa={isJa}
            total={total}
            shown={visible.length}
          />
          {pageCount > 1 && (
            <FloatingPager
              onPrev={() => setPageAndUrl(page - 1)}
              onNext={() => setPageAndUrl(page + 1)}
              atFirst={page <= 1}
              atLast={page >= pageCount}
              isJa={isJa}
              accent="#9333EA"
              testIdPrefix="whats-new-floating-pager"
            />
          )}
        </div>
        </div>
        {/* ═════════ END MIDDLE CONTENT (side aura wrapper closes) ════════ */}

        {/* PUBLIC SAFETY NOTE — compact card (no big dark box) */}
        <section className="bg-slate-50 border-t border-slate-200" data-testid="whats-new-safety-footer">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="relative rounded-xl bg-white ring-1 ring-slate-200 px-5 py-4 overflow-hidden">
              <span
                aria-hidden
                className="absolute top-0 left-0 right-0 h-1"
                style={{
                  background:
                    "linear-gradient(90deg, #5B2E83 0%, #2F8F3C 50%, #0E5FB0 100%)",
                }}
              />
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-start gap-2.5">
                  <ShieldCheck className="h-4 w-4 text-slate-700 mt-0.5 shrink-0" strokeWidth={2} />
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900 leading-snug">
                      {isJa ? "公開更新と、保護される情報" : "Public updates, protected details."}
                    </h2>
                    <p className="text-[12.5px] text-slate-600 leading-relaxed mt-0.5">
                      {isJa
                        ? "このページには、公開が承認された情報のみを表示しています。内部計画、個人情報、機密性の高い運用情報は表示されません。"
                        : "This page shows approved public updates only. Internal plans, private customer information, and sensitive operational details are not displayed."}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/${lang}/contact`}
                  className="ml-auto shrink-0 inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#9333EA] text-white text-xs font-semibold hover:bg-[#7C3AED] transition-colors whitespace-nowrap"
                >
                  {isJa ? "お問い合わせ" : "Contact us"}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
